
import { useState, useEffect } from 'react';
import BottomNavigation from '@/components/BottomNavigation';
import TopNavigation from '@/components/TopNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Copy, Gift, Clock, Percent, Tag } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface Promo {
  id: string;
  code: string;
  description: string;
  discount_type: string;
  discount_value: number;
  min_order_amount: number | null;
  max_discount: number | null;
  start_date: string;
  end_date: string;
  usage_limit: number | null;
  used_count: number;
  is_active: boolean;
}

const PromoPage = () => {
  const [promos, setPromos] = useState<Promo[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    fetchActivePromos();
  }, []);

  const fetchActivePromos = async () => {
    try {
      const { data, error } = await supabase
        .from('promos')
        .select('*')
        .eq('is_active', true)
        .eq('is_public', true)
        .gte('end_date', new Date().toISOString().split('T')[0])
        .lte('start_date', new Date().toISOString().split('T')[0])
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching promos:', error);
        toast({
          title: "Error",
          description: "Failed to load promotions",
          variant: "destructive",
        });
        return;
      }

      // Filter for restaurant business type
      const restaurantPromos = (data || []).filter((promo: any) => 
        !promo.business_type || promo.business_type === 'restaurant'
      );
      setPromos(restaurantPromos);
    } catch (error) {
      console.error('Error fetching promos:', error);
      toast({
        title: "Error",
        description: "Failed to load promotions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyPromoCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast({
        title: t('copied'),
        description: `${t('promo_code_copied')}: "${code}"`,
      });
    } catch (error) {
      toast({
        title: t('error'),
        description: "Failed to copy promo code",
        variant: "destructive",
      });
    }
  };

  const formatDiscount = (promo: Promo) => {
    // Check if it's a free delivery promo by discount_type
    if (promo.discount_type === 'free_delivery') {
      return 'Free Delivery';
    }
    
    if (promo.discount_type === 'percentage') {
      return `${promo.discount_value}% ${t('off')}`;
    } else {
      return `D${promo.discount_value} ${t('off')}`;
    }
  };

  const formatEndDate = (endDate: string) => {
    return new Date(endDate).toLocaleDateString();
  };

  const isExpiringSoon = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 3;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <TopNavigation />
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <TopNavigation />
      
      <div className="px-4 py-6">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Tag className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('special_offers')}</h1>
          <p className="text-gray-600 text-lg">
            {t('save_more_meals')}
          </p>
        </div>

        {promos.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Gift className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-3">{t('no_active_promotions')}</h3>
            <p className="text-gray-500 text-base">
              {t('check_back_deals')}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {promos.map((promo) => (
              <Card key={promo.id} className="overflow-hidden shadow-sm border-0 bg-white">
                <div className="relative">
                  <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                            <Percent className="h-6 w-6" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold">
                              {formatDiscount(promo)}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 font-mono text-sm">
                                {promo.code}
                              </Badge>
                              {isExpiringSoon(promo.end_date) && (
                                <Badge className="bg-yellow-400 text-yellow-900 text-xs">
                                  {t('expires_soon')}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <p className="text-gray-700 text-base mb-4 leading-relaxed">{promo.description}</p>
                    
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-4">
                      {promo.min_order_amount && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{t('minimum_order')}</span>
                          <span className="font-medium">D{promo.min_order_amount}</span>
                        </div>
                      )}
                      
                      {promo.max_discount && promo.discount_type === 'percentage' && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{t('maximum_discount')}</span>
                          <span className="font-medium">D{promo.max_discount}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {t('valid_until')}
                        </span>
                        <span className="font-medium">{formatEndDate(promo.end_date)}</span>
                      </div>
                      
                      {promo.usage_limit && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{t('remaining_uses')}</span>
                          <span className="font-medium">{promo.usage_limit - promo.used_count}</span>
                        </div>
                      )}
                    </div>
                    
                    <Button
                      onClick={() => copyPromoCode(promo.code)}
                      className="w-full bg-red-600 hover:bg-red-700 text-white py-3 text-base font-medium"
                      size="lg"
                    >
                      <Copy className="h-5 w-5 mr-2" />
                      {t('copy_code')}: {promo.code}
                    </Button>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default PromoPage;
